import { useEffect, useState } from "react";

import { json, redirect, type ActionFunctionArgs } from "@remix-run/node";
import {
    Link,
    useActionData,
    useNavigate,
    useSearchParams,
    useNavigation,
} from "@remix-run/react";

import Layout from "~/components/layout";
import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { P } from "~/components/ui/typography";
import { toast } from "~/components/ui/use-toast";
import { withCors } from "~/config/cors";
import { supabase } from "~/integrations/supabase/ssr.browser";
import { createSupabaseServerClient } from "~/integrations/supabase/ssr.server";
import { PRODUCT_NAME } from "~/lib/env.shared";
import { logger } from "~/lib/logger";
import { trackEvent } from "~/modules.server/analytics";
import { practitionersRepository } from "~/modules.server/profile/practitioners-repository";
import type { AuthError } from "~/types";
import { pageMeta } from "~/utils/meta";
import { assertIsPost } from "~/utils.server/http.server";

export const meta = pageMeta(
    `${PRODUCT_NAME} - Sign In`,
    `Sign in to your ${PRODUCT_NAME} account.`,
);

export const loader = withCors(async () => json({}));

export const action = withCors(async ({ request }: ActionFunctionArgs) => {
    assertIsPost(request);
    const formData = await request.formData();

    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const redirectTo = formData.get("redirectTo") as string;

    // AIDEV-NOTE: Use SSR server client for authentication - single auth call
    const { supabase, headers } = createSupabaseServerClient(request);

    const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
    });

    if (error || !data.session) {
        return json(
            { error: error?.message || "Login failed" },
            { status: 400, headers },
        );
    }
    trackEvent("logged in", {
        userId: data.user.id,
    });

    // AIDEV-NOTE: Check if user is a practitioner
    const practitioner = await practitionersRepository.getPractitionerById({
        profileId: data.user.id,
        supabaseClient: supabase,
    });

    // If user has a practitioner record, redirect to /practitioner
    const finalRedirectTo = redirectTo
        ? redirectTo
        : practitioner
          ? "/practitioner"
          : "/sessions";

    // AIDEV-NOTE: Using SSR authentication only - legacy auth removed
    return redirect(finalRedirectTo, {
        headers,
    });
});

const Login = () => {
    const navigate = useNavigate();
    const navigation = useNavigation();
    const actionData = useActionData<{ error?: string }>();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [searchParams] = useSearchParams();
    const redirectTo = searchParams.get("redirect");

    const isSubmitting = navigation.state === "submitting";

    // AIDEV-NOTE: Log navigation state to debug loading button
    useEffect(() => {
        logger
            .withMetadata({ navigationState: navigation.state, isSubmitting })
            .info("Navigation state");
    }, [navigation.state, isSubmitting]);

    // Check for email confirmation error and show modal
    useEffect(() => {
        if (actionData?.error === "Email not confirmed") {
            setShowConfirmationModal(true);
        }
    }, [actionData]);

    const [isResending, setIsResending] = useState(false);

    async function handleResendConfirmation() {
        try {
            setIsResending(true);
            const emailInput = document.querySelector(
                'input[name="email"]',
            ) as HTMLInputElement;
            const email = emailInput?.value || "";

            const { error } = await supabase.auth.resend({
                type: "signup",
                email: email,
            });

            if (error) {
                const authError = error as AuthError;
                toast({
                    title: "Error",
                    description:
                        authError.message ||
                        "Failed to send confirmation email",
                    variant: "destructive",
                });
            } else {
                toast({
                    title: "Success",
                    description:
                        "Confirmation email sent. Please check your inbox.",
                });
                navigate(
                    `/email-confirmation?email=${encodeURIComponent(email)}`,
                );
                setShowConfirmationModal(false);
            }
        } catch (error) {
            const authError = error as AuthError;
            toast({
                title: "Error",
                description:
                    authError.message || "Failed to send confirmation email",
                variant: "destructive",
            });
        } finally {
            setIsResending(false);
        }
    }

    return (
        <Layout backNavOverride="/landing" title="Sign in" narrow={true}>
            <div className="mx-auto w-full max-w-sm space-y-6 ">
                <form method="POST" action="/login" className="space-y-4">
                    <div>
                        <Input
                            name="email"
                            type="email"
                            placeholder="Enter your email"
                            required
                        />
                    </div>
                    <div>
                        <Input
                            name="password"
                            type="password"
                            placeholder="Enter your password"
                            required
                            minLength={6}
                        />
                    </div>

                    {/* Hidden field for Remix form submission */}
                    {redirectTo && (
                        <input
                            type="hidden"
                            name="redirectTo"
                            value={redirectTo}
                        />
                    )}

                    {actionData?.error && (
                        <P className="text-sm text-destructive">
                            {actionData.error}
                        </P>
                    )}

                    <Button
                        type="submit"
                        className="w-full"
                        isLoading={isSubmitting}
                    >
                        Sign in
                    </Button>
                    <div className="text-center text-sm">
                        <Link
                            to="/signup"
                            className="text-rose-500 underline hover:underline"
                        >
                            <Button variant="subtle" className="w-full">
                                Don't have an account? Sign up
                            </Button>
                        </Link>
                    </div>
                    <div className="text-center">
                        <Link
                            to="/reset-password"
                            className="text-sm underline hover:underline"
                        >
                            <Button variant="ghost" className="w-full">
                                Forgot your password?
                            </Button>
                        </Link>
                    </div>
                </form>

                <div className="space-y-4" />
            </div>

            <Dialog
                open={showConfirmationModal}
                onOpenChange={setShowConfirmationModal}
                analyticsId={"resend-confirmation-email"}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Email not confirmed</DialogTitle>
                        <DialogDescription>
                            Your email address has not been confirmed yet. Would
                            you like to resend the confirmation email?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowConfirmationModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleResendConfirmation}
                            isLoading={isResending}
                        >
                            Resend confirmation
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
};

export default Login;
